import { API_ENDPOINTS } from '@/constants/api'
import { apiClient } from '@/lib/api/client'

/**
 * Leads API Service
 * Handles lead-related API calls
 */
export const leadsApi = {
  /**
   * Get leads list with pagination and filters
   * @param {Object} params - { page, limit, status, state, source, assigned_to, created_by, search, ordering }
   * @returns {Promise<Object>} Leads list with pagination
   */
  getLeads: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.LEADS.LIST, {
      params: {
        ...params,
        expand: 'assigned_to,created_by',
      }
    })
    return response
  },

  /**
   * Get single lead by UUID
   * @param {string} uuid - Lead UUID
   * @returns {Promise<Object>} Lead details
   */
  getLead: async (uuid) => {
    const response = await apiClient.get(
      API_ENDPOINTS.LEADS.DETAIL.replace(':uuid', uuid),
      { params: { expand: 'assigned_to,created_by,buyer,arendator' } }
    )
    return response
  },

  /**
   * Create new lead
   * @param {Object} data - Lead data
   * @returns {Promise<Object>} Created lead
   */
  createLead: async (data) => {
    const response = await apiClient.post(
      `${API_ENDPOINTS.LEADS.CREATE}?expand=assigned_to,created_by,buyer,arendator`,
      data
    )
    return response
  },

  /**
   * Update lead
   * @param {string} uuid - Lead UUID
   * @param {Object} data - Updated lead data
   * @returns {Promise<Object>} Updated lead
   */
  updateLead: async (uuid, data) => {
    const response = await apiClient.put(
      `${API_ENDPOINTS.LEADS.UPDATE.replace(':uuid', uuid)}?expand=assigned_to,created_by,buyer,arendator`,
      data
    )
    return response
  },

  /**
   * Delete lead
   * @param {string} uuid - Lead UUID
   * @returns {Promise<void>}
   */
  deleteLead: async (uuid) => {
    const response = await apiClient.delete(API_ENDPOINTS.LEADS.DELETE.replace(':uuid', uuid))
    return response
  },

  /**
   * Get leads statistics
   * @returns {Promise<Object>} Statistics object
   */
  getLeadsStats: async () => {
    const response = await apiClient.get(API_ENDPOINTS.LEADS.STATS)
    return response
  },

  /**
   * Get users for autocomplete
   * @param {Object} params - { search, page, page_size }
   * @returns {Promise<Object>} Users list with pagination
   */
  getUsersAutocomplete: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.USERS.AUTOCOMPLETE, { params })
    return response
  },

  /**
   * Get buyers for autocomplete
   * @param {Object} params - { search, page, page_size }
   * @returns {Promise<Object>} Buyers list with pagination
   */
  getBuyersAutocomplete: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.BUYERS.AUTOCOMPLETE, { params })
    return response
  },

  /**
   * Get arendators for autocomplete
   * @param {Object} params - { search, page, page_size }
   * @returns {Promise<Object>} Arendators list with pagination
   */
  getArendatorsAutocomplete: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.ARENDATORS.AUTOCOMPLETE, { params })
    return response
  },

  /**
   * Get user by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} User object
   */
  getUserById: async (userId) => {
    // Use autocomplete endpoint with ID filter
    const response = await apiClient.get(API_ENDPOINTS.USERS.AUTOCOMPLETE, {
      params: { id: userId }
    })
    return response?.data?.[0] || null
  },

  /**
   * Get multiple users by IDs
   * @param {Array<number>} userIds - Array of user IDs
   * @returns {Promise<Object>} Object with userId as key and user object as value
   */
  getUsersByIds: async (userIds) => {
    if (!userIds || userIds.length === 0) return {}

    try {
      // Try with ids parameter (comma-separated)
      const response = await apiClient.get(API_ENDPOINTS.USERS.AUTOCOMPLETE, {
        params: {
          ids: userIds.join(','),
          page_size: 100 // Get all users at once
        }
      })

      // Convert array to object with ID as key
      const usersMap = {}
      if (response?.data) {
        response.data.forEach(user => {
          if (user.id || user.user_id || user.pk) {
            const userId = user.id || user.user_id || user.pk
            usersMap[userId] = user
          }
        })
      }

      return usersMap
    } catch (error) {
      console.error('Failed to load users by IDs:', error)
      return {}
    }
  },

  /**
   * Get buyer by ID
   * @param {number} buyerId - Buyer ID
   * @returns {Promise<Object>} Buyer object
   */
  getBuyerById: async (buyerId) => {
    const response = await apiClient.get(API_ENDPOINTS.BUYERS.AUTOCOMPLETE, {
      params: { id: buyerId }
    })
    return response?.data?.[0] || null
  },

  /**
   * Get multiple buyers by IDs
   * @param {Array<number>} buyerIds - Array of buyer IDs
   * @returns {Promise<Object>} Object with buyerId as key and buyer object as value
   */
  getBuyersByIds: async (buyerIds) => {
    if (!buyerIds || buyerIds.length === 0) return {}

    try {
      const response = await apiClient.get(API_ENDPOINTS.BUYERS.AUTOCOMPLETE, {
        params: {
          ids: buyerIds.join(','),
          page_size: 100
        }
      })

      const buyersMap = {}
      if (response?.data) {
        response.data.forEach(buyer => {
          const buyerId = buyer.id || buyer.buyer_id || buyer.pk
          if (buyerId) {
            buyersMap[buyerId] = buyer
          }
        })
      }

      return buyersMap
    } catch (error) {
      console.error('Failed to load buyers by IDs:', error)
      return {}
    }
  },

  /**
   * Get arendator by ID
   * @param {number} arendatorId - Arendator ID
   * @returns {Promise<Object>} Arendator object
   */
  getArendatorById: async (arendatorId) => {
    const response = await apiClient.get(API_ENDPOINTS.ARENDATORS.AUTOCOMPLETE, {
      params: { id: arendatorId }
    })
    return response?.data?.[0] || null
  },

  /**
   * Get multiple arendators by IDs
   * @param {Array<number>} arendatorIds - Array of arendator IDs
   * @returns {Promise<Object>} Object with arendatorId as key and arendator object as value
   */
  getArendatorsByIds: async (arendatorIds) => {
    if (!arendatorIds || arendatorIds.length === 0) return {}

    try {
      const response = await apiClient.get(API_ENDPOINTS.ARENDATORS.AUTOCOMPLETE, {
        params: {
          ids: arendatorIds.join(','),
          page_size: 100
        }
      })

      const arendatorsMap = {}
      if (response?.data) {
        response.data.forEach(arendator => {
          const arendatorId = arendator.id || arendator.arendator_id || arendator.pk
          if (arendatorId) {
            arendatorsMap[arendatorId] = arendator
          }
        })
      }

      return arendatorsMap
    } catch (error) {
      console.error('Failed to load arendators by IDs:', error)
      return {}
    }
  },
}

export default leadsApi
